import { useState, useEffect, useCallback } from 'react';
import {
  SubmitHandler,
  useDocumentTitle,
  FormProvider,
  useForm,
} from '@leagueplatform/web-common';
import { useIntl } from '@leagueplatform/locales';
import {
  Box,
  Button,
  HeadingText,
  StackLayout,
  StackItem,
  TextAction,
  UtilityText,
  VisuallyHidden,
} from '@leagueplatform/genesis-core';
import {
  LoadingIndicator,
  Switch,
} from '@leagueplatform/web-common-components';
import {
  usePageViewAnalytics,
  SCREEN_NAMES,
  PRODUCT_AREA,
  trackAnalyticsEvent,
  EVENT_NAME,
} from '@leagueplatform/analytics';
import { captureAppError } from '@leagueplatform/observability';
import { Link } from 'react-router-dom';
import { PageHeader } from 'components/page-header/page-header.component';
import { ContentSection } from 'components/content-section/content-section.component';
import { CONTENT_MAX_WIDTH } from 'common/constants/ui';
import {
  ROUTE_PATHS,
  APP_NAME,
  SENTRY_CONSTANTS,
} from 'common/constants/constants';
import { useIsBelowTablet } from 'hooks/use-is-below-tablet/use-is-below-tablet.hooks';
import { useGetScanUserProfile } from 'hooks/use-get-scan-user-profile/use-get-scan-user-profile.hook';
import { useUpdateUserProfile } from 'hooks/use-set-user-profile/use-set-user-profile.hook';
import { TOAST_STATUS, toastActions } from '@leagueplatform/toast-messages';
import { ResponsiveButtonRow } from 'components/responsive-button-row/responsive-button-row.component';
import { phoneNumberValidation } from 'common/utils/rhf-form-validation.util';
import { RHFTextInput } from 'components/form-elements/rhf-text-input.component';
import { TextCommunicationConsentText } from 'components/modals/text-communication-terms/text-communication-consent-text.component';
import { PHONE_NUMBER_TYPES } from 'common/constants/account-info';

const TextNotificationsLabel = () => {
  const { formatMessage } = useIntl();
  return (
    <>
      <UtilityText
        css={{
          display: 'block',
          marginBlockEnd: '$half',
        }}
        emphasis="emphasized"
      >
        {formatMessage({ id: 'STR_ALLOW_TEXT_NOTIFICATIONS' })}
      </UtilityText>
      <UtilityText>
        {formatMessage(
          { id: 'STR_TEXT_INVITATIONS_AND_UPDATES' },
          { appName: APP_NAME },
        )}
      </UtilityText>
    </>
  );
};

type TextNotificationsFormValues = {
  mobilePhoneNumber: string;
};

export const CommunicationPreferencesPage = () => {
  const { formatMessage } = useIntl();
  const isBelowTablet = useIsBelowTablet();
  useDocumentTitle(formatMessage({ id: 'STR_COMMUNICATION_PREFERENCES' }));
  usePageViewAnalytics({
    product_area: PRODUCT_AREA.SETTINGS,
    screen_name: SCREEN_NAMES.COMMUNICATION_PREFERENCES,
  });

  const {
    data: userProfileData,
    isLoading,
    isRefetching,
  } = useGetScanUserProfile();
  const { phoneNumbers } = userProfileData || {};
  const { home, mobile, alternate } = phoneNumbers || {};
  const homePhoneNumber = home?.number || '';
  const mobilePhoneNumber = mobile?.number || '';
  const alternatePhoneNumber = alternate?.number || '';
  const isTextNotificationsDisabled =
    userProfileData?.userProfile?.notificationPreferences?.disableSms;

  const [textNotificationSwitch, setTextNotificationSwitch] = useState<boolean>(
    !isTextNotificationsDisabled,
  );

  const [isEditing, setIsEditing] = useState<boolean>(false);

  const methods = useForm<TextNotificationsFormValues>({
    defaultValues: {
      mobilePhoneNumber,
    },
  });

  const { updateUserProfile } = useUpdateUserProfile({
    onSuccess: () => {
      toastActions.add({
        type: TOAST_STATUS.SUCCESS,
        textId: 'STR_TEXT_PREFERENCE_UPDATED',
      });

      // The user is opting out of text notifications
      if (!isEditing) {
        methods.resetField('mobilePhoneNumber', {
          defaultValue: '',
        });
        // Reset the form with new default values
        methods.reset();
      }

      // Close the form
      setIsEditing(false);
    },
    onError: () => {
      toastActions.add({
        type: TOAST_STATUS.ERROR,
        textId: 'STR_TEXT_PREFERENCE_UPDATED_FAILED',
      });
    },
  });

  useEffect(() => {
    setTextNotificationSwitch(!isTextNotificationsDisabled);
  }, [isTextNotificationsDisabled]);

  const handleDocumentPreferencesClick = useCallback(() => {
    trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
      product_area: PRODUCT_AREA.SETTINGS,
      screen_name: SCREEN_NAMES.COMMUNICATION_PREFERENCES,
      detail: 'manage document preferences',
    });
  }, []);

  const handleFormSubmit: SubmitHandler<TextNotificationsFormValues> =
    useCallback(
      async (values) => {
        const { mobilePhoneNumber: mobilePhoneNumberFormValue } = values;
        const updateTextNotifications = async (mobileNumber?: string) => {
          try {
            const phoneNumberValues = [
              {
                description: PHONE_NUMBER_TYPES.MOBILE,
                number: mobileNumber || '',
              },
            ];
            if (homePhoneNumber) {
              phoneNumberValues.push({
                description: PHONE_NUMBER_TYPES.HOME,
                number: homePhoneNumber,
              });
            }
            if (alternatePhoneNumber) {
              phoneNumberValues.push({
                description: PHONE_NUMBER_TYPES.ALTERNATE,
                number: alternatePhoneNumber,
              });
            }
            await updateUserProfile({
              phoneNumbers: phoneNumberValues,
              notificationPreferences: { disableSms: !mobileNumber },
            });
          } catch (error) {
            if (error instanceof Error) {
              captureAppError(error, {
                ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
                errorName: 'Error saving Text Notifications',
                context: {
                  Context: {
                    description: formatMessage({
                      id: 'STR_COMMUNICATION_PREFERENCES',
                    }),
                    disableSMSNotifications: !mobileNumber,
                    hasPhoneNumber: Boolean(mobilePhoneNumber),
                  },
                },
              });
            }
          }
        };

        // if the mobile number is not being edited and the form was submited, handle disabling SMS notifications
        if (!isEditing) {
          await updateTextNotifications();
        } else {
          // if the mobile number is being edited and the form was submited, handle enabling SMS notifications
          await updateTextNotifications(mobilePhoneNumberFormValue);
        }
      },
      [
        alternatePhoneNumber,
        homePhoneNumber,
        mobilePhoneNumber,
        isEditing,
        formatMessage,
        updateUserProfile,
      ],
    );

  const handleToggleTextNotification = useCallback(() => {
    const nextSwitchValue = !textNotificationSwitch;

    trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
      product_area: PRODUCT_AREA.SETTINGS,
      screen_name: SCREEN_NAMES.COMMUNICATION_PREFERENCES,
      detail: 'text notification toggle',
      toggle_status: nextSwitchValue ? 'on' : 'off',
    });

    setTextNotificationSwitch((prev: boolean) => !prev);
    setIsEditing(nextSwitchValue);

    // The user is opt-ing out of text notifications
    if (nextSwitchValue === false && isTextNotificationsDisabled === false) {
      methods.handleSubmit(handleFormSubmit)();
    }
  }, [
    textNotificationSwitch,
    methods,
    handleFormSubmit,
    isTextNotificationsDisabled,
  ]);

  const handleCancelEditForm = () => {
    setIsEditing(false);
    methods.resetField('mobilePhoneNumber', {
      defaultValue: mobilePhoneNumber,
    });
    setTextNotificationSwitch(!isTextNotificationsDisabled);
  };

  if (isLoading || isTextNotificationsDisabled === undefined)
    return <LoadingIndicator />;

  const displayMobilePhoneNumberEdit = Boolean(mobilePhoneNumber) && !isEditing;

  // Display the loading state for the switch user is opting out of text notifications
  const displaySwitchLoadingState =
    (isRefetching || (methods.formState.isSubmitting && !isEditing)) &&
    textNotificationSwitch === false;

  return (
    <Box
      css={{
        display: 'grid',
        gridGap: '$three',
        gridTemplateRows: 'auto 1fr',
        paddingBlockEnd: isBelowTablet ? undefined : '$three',
      }}
    >
      <PageHeader
        text={formatMessage({ id: 'STR_COMMUNICATION_PREFERENCES' })}
      />
      <Box css={{ paddingInline: '$two', paddingBlockEnd: '$five' }}>
        <Box
          css={{
            display: isBelowTablet ? 'flex' : 'grid',
            flexDirection: isBelowTablet ? 'column' : undefined,
            gridTemplateColumns: '3fr 2fr',
            justifyContent: 'start',
            gap: '$oneAndHalf',
            alignItems: isBelowTablet ? 'stretch' : 'start',
            maxWidth: CONTENT_MAX_WIDTH,
            marginInline: 'auto',
          }}
        >
          <ContentSection
            css={{
              paddingInlineStart: '$two',
              height: 'auto',
              margin: 0,
            }}
            horizontalAlignment="stretch"
          >
            <StackLayout spacing="$half" css={{ height: 'auto' }}>
              <HeadingText
                level="2"
                size="md"
                css={{ paddingInlineStart: '$half' }}
              >
                {formatMessage({ id: 'STR_NOTIFICATIONS' })}
              </HeadingText>
              <Switch
                css={{
                  '.cc-switch-root-wrapper': {
                    display: 'flex',
                    alignSelf: 'start',
                  },
                }}
                id="switch--text-notifications"
                label={<TextNotificationsLabel />}
                checked={textNotificationSwitch}
                layout="row"
                onChange={handleToggleTextNotification}
                hideIcon
                disabled={isEditing}
                loading={displaySwitchLoadingState}
              />
              {isEditing && (
                <FormProvider formMethods={methods} onSubmit={handleFormSubmit}>
                  <StackLayout
                    css={{ paddingInline: '$half' }}
                    spacing="$two"
                    horizontalAlignment="stretch"
                  >
                    <StackItem
                      css={{ maxWidth: 327, marginInlineEnd: '$three' }}
                    >
                      <RHFTextInput
                        id="mobilePhoneNumber"
                        type="tel"
                        placeholder={formatMessage({
                          id: 'STR_MOBILE_PHONE_NUMBER_PLACEHOLDER',
                        })}
                        required
                        requiredMessage={formatMessage({
                          id: mobilePhoneNumber
                            ? 'STR_OPTED_IN_MOBILE_PHONE_NUMBER_REQUIRED'
                            : 'STR_MOBILE_PHONE_NUMBER_REQUIRED',
                        })}
                        label={formatMessage({
                          id: 'STR_MOBILE_PHONE_NUMBER_LABEL',
                        })}
                        registerOptions={phoneNumberValidation(
                          formatMessage({ id: 'STR_PHONE_VALIDATION_ERROR' }),
                        )}
                      />
                    </StackItem>

                    <TextCommunicationConsentText
                      css={{ marginInlineEnd: '$three' }}
                    />
                    <ResponsiveButtonRow
                      primaryBtn={
                        <Button
                          type="submit"
                          loading={methods.formState.isSubmitting}
                        >
                          {formatMessage({
                            id: 'STR_ALLOW_TEXT_NOTIFICATIONS',
                          })}
                        </Button>
                      }
                      secondaryBtn={
                        <Button
                          priority="secondary"
                          onClick={handleCancelEditForm}
                        >
                          {formatMessage({ id: 'STR_CANCEL_EDIT' })}
                        </Button>
                      }
                      css={{ paddingBlockStart: '$half' }}
                    />
                  </StackLayout>
                </FormProvider>
              )}
              {displayMobilePhoneNumberEdit && (
                <StackLayout
                  orientation="horizontal"
                  horizontalAlignment="spaceBetween"
                  verticalAlignment="center"
                  css={{
                    paddingInline: '$half',
                    width: '100%',
                  }}
                >
                  <StackItem>
                    <UtilityText
                      size="sm"
                      css={{ color: '$onSurfaceTextSubdued' }}
                    >
                      {formatMessage({ id: 'STR_MOBILE_PHONE_NUMBER' })}
                    </UtilityText>
                    <UtilityText css={{ fontWeight: 'bold' }}>
                      {mobilePhoneNumber}
                    </UtilityText>
                  </StackItem>
                  <TextAction
                    as="button"
                    icon="interfaceEdit"
                    onClick={() => setIsEditing(true)}
                  >
                    {formatMessage({ id: 'STR_EDIT' })}
                    <VisuallyHidden>
                      {formatMessage({ id: 'STR_MOBILE_PHONE_NUMBER' })}
                    </VisuallyHidden>
                  </TextAction>
                </StackLayout>
              )}
            </StackLayout>
          </ContentSection>

          <ContentSection
            css={{ height: 'auto', margin: 0 }}
            horizontalAlignment="stretch"
          >
            <StackLayout spacing="$half" css={{ height: 'auto' }}>
              <HeadingText level="2" size="md">
                {formatMessage({ id: 'STR_DOCUMENT_PREFERENCES' })}
              </HeadingText>
              <TextAction
                as={Link}
                to={ROUTE_PATHS.DOCUMENT_PREFERENCES()}
                icon="illustrativeMobileAlertUser"
                indicator="disclosure"
                onClick={handleDocumentPreferencesClick}
                css={{
                  alignItems: 'start',
                  gap: '$half',
                  '.GDS-icon': {
                    marginBlockStart: '$half',
                    width: '$twoAndHalf',
                  },
                  '@mobile': {
                    gap: '$quarter',
                  },
                }}
              >
                {formatMessage({ id: 'STR_MANAGE_DOCUMENT_PREFERENCES' })}
              </TextAction>
            </StackLayout>
          </ContentSection>
        </Box>
      </Box>
    </Box>
  );
};
